/**
 * Tarjeta de Insights de Inventario
 * 
 * Análisis inteligente de inventario con alertas automáticas,
 * predicciones de stock y recomendaciones de reposición.
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Collapse,
  LinearProgress,
  Avatar,
  Divider,
  Button,
  useTheme
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  ShoppingCart as ShoppingCartIcon,
  Speed as SpeedIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

/**
 * Componente para mostrar estado de stock de un producto
 */
const ProductStockItem = ({ product, onAction }) => {
  const theme = useTheme();
  
  const getStockStatus = () => {
    const { stock, minStock, maxStock } = product;
    if (stock <= 0) return { status: 'out', color: 'error', icon: <ErrorIcon /> };
    if (stock <= minStock) return { status: 'low', color: 'warning', icon: <WarningIcon /> };
    if (stock >= maxStock) return { status: 'high', color: 'info', icon: <TrendingUpIcon /> };
    return { status: 'normal', color: 'success', icon: <CheckCircleIcon /> };
  };

  const stockStatus = getStockStatus();
  const stockPercentage = product.maxStock > 0 ? (product.stock / product.maxStock) * 100 : 0;

  return (
    <ListItem>
      <Avatar sx={{ bgcolor: `${stockStatus.color}.main`, mr: 2 }}>
        {stockStatus.icon}
      </Avatar>
      <ListItemText
        primary={
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" noWrap sx={{ maxWidth: '60%' }}>
              {product.name}
            </Typography>
            <Chip
              size="small"
              label={`${product.stock} unidades`}
              color={stockStatus.color}
              variant="outlined"
            />
          </Box>
        }
        secondary={
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" color="text.secondary">
                Min: {product.minStock} | Max: {product.maxStock}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Rotación: {product.turnoverRate || 0} días
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(stockPercentage, 100)}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: theme.palette.grey[200],
                '& .MuiLinearProgress-bar': {
                  backgroundColor: theme.palette[stockStatus.color].main,
                  borderRadius: 3
                }
              }}
            />
            {product.daysUntilStockout && (
              <Typography variant="caption" color="error.main" sx={{ mt: 0.5 }}>
                Stock agotado en ~{product.daysUntilStockout} días
              </Typography>
            )}
          </Box>
        }
      />
      <ListItemSecondaryAction>
        {stockStatus.status === 'low' || stockStatus.status === 'out' ? (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => onAction?.(product, 'restock')}
          >
            Reponer
          </Button>
        ) : null}
      </ListItemSecondaryAction>
    </ListItem>
  );
};

/**
 * Componente para mostrar métricas de inventario
 */
const InventoryMetrics = ({ data }) => {
  const {
    totalProducts = 0,
    lowStockProducts = 0,
    outOfStockProducts = 0,
    overStockProducts = 0,
    averageTurnover = 0,
    inventoryValue = 0
  } = data;

  const metrics = [
    {
      title: 'Total Productos',
      value: totalProducts,
      icon: <InventoryIcon />,
      color: 'primary'
    },
    {
      title: 'Stock Bajo',
      value: lowStockProducts,
      icon: <WarningIcon />,
      color: 'warning'
    },
    {
      title: 'Sin Stock',
      value: outOfStockProducts,
      icon: <ErrorIcon />,
      color: 'error'
    },
    {
      title: 'Sobre Stock',
      value: overStockProducts,
      icon: <TrendingUpIcon />,
      color: 'info'
    }
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {metrics.map((metric, index) => (
        <Grid item xs={6} md={3} key={index}>
          <Box textAlign="center">
            <Avatar sx={{ bgcolor: `${metric.color}.main`, mx: 'auto', mb: 1 }}>
              {metric.icon}
            </Avatar>
            <Typography variant="h6">{metric.value}</Typography>
            <Typography variant="caption" color="text.secondary">
              {metric.title}
            </Typography>
          </Box>
        </Grid>
      ))}
      
      <Grid item xs={6} md={3}>
        <Box textAlign="center">
          <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 1 }}>
            <SpeedIcon />
          </Avatar>
          <Typography variant="h6">{averageTurnover.toFixed(1)}</Typography>
          <Typography variant="caption" color="text.secondary">
            Rotación Promedio (días)
          </Typography>
        </Box>
      </Grid>
      
      <Grid item xs={6} md={3}>
        <Box textAlign="center">
          <Avatar sx={{ bgcolor: 'secondary.main', mx: 'auto', mb: 1 }}>
            <ShoppingCartIcon />
          </Avatar>
          <Typography variant="h6">
            {inventoryValue.toLocaleString('es-AR', {
              style: 'currency',
              currency: 'ARS',
              maximumFractionDigits: 0
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Valor Inventario
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

/**
 * Componente principal de insights de inventario
 */
const InventoryInsightsCard = ({ data = {}, onProductAction }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  // Extraer datos del objeto
  const {
    products = [],
    metrics = {},
    alerts = [],
    recommendations = []
  } = data;

  // Procesar productos por prioridad
  const processedProducts = useMemo(() => {
    return products
      .map(product => ({
        ...product,
        priority: product.stock <= 0 ? 3 : product.stock <= product.minStock ? 2 : 1
      }))
      .sort((a, b) => b.priority - a.priority);
  }, [products]);

  // Generar alertas automáticas
  const generateAlerts = () => {
    const autoAlerts = [];
    
    const outOfStock = processedProducts.filter(p => p.stock <= 0);
    const lowStock = processedProducts.filter(p => p.stock > 0 && p.stock <= p.minStock);
    
    if (outOfStock.length > 0) {
      autoAlerts.push({
        type: 'error',
        title: 'Productos sin stock',
        message: `${outOfStock.length} productos están agotados y requieren reposición inmediata`
      });
    }
    
    if (lowStock.length > 0) {
      autoAlerts.push({
        type: 'warning',
        title: 'Stock bajo',
        message: `${lowStock.length} productos tienen stock bajo y necesitan reposición pronto`
      });
    }
    
    const fastMoving = processedProducts.filter(p => p.turnoverRate && p.turnoverRate < 7);
    if (fastMoving.length > 0) {
      autoAlerts.push({
        type: 'info',
        title: 'Productos de alta rotación',
        message: `${fastMoving.length} productos tienen alta rotación. Considera aumentar stock mínimo`
      });
    }
    
    return [...autoAlerts, ...alerts];
  };

  const allAlerts = generateAlerts();

  // Generar recomendaciones automáticas
  const generateRecommendations = () => {
    const autoRecs = [];
    
    const slowMoving = processedProducts.filter(p => p.turnoverRate && p.turnoverRate > 30);
    if (slowMoving.length > 0) {
      autoRecs.push({
        title: 'Optimizar productos de baja rotación',
        description: `${slowMoving.length} productos tienen baja rotación. Considera promociones o descuentos`
      });
    }
    
    const overStock = processedProducts.filter(p => p.stock > p.maxStock);
    if (overStock.length > 0) {
      autoRecs.push({
        title: 'Reducir sobre-stock',
        description: `${overStock.length} productos tienen exceso de inventario. Evalúa estrategias de liquidación`
      });
    }
    
    return [...autoRecs, ...recommendations];
  };

  const allRecommendations = generateRecommendations();

  return (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <InventoryIcon />
            <Typography variant="h6">Gestión de Inventario</Typography>
          </Box>
        }
        subheader="Análisis inteligente de stock y recomendaciones"
        action={
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: theme.transitions.create('transform', {
                duration: theme.transitions.duration.shortest,
              }),
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        }
      />
      
      <CardContent>
        {/* Métricas principales */}
        <InventoryMetrics data={metrics} />

        {/* Alertas críticas */}
        {allAlerts.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Alertas de Inventario
            </Typography>
            {allAlerts.slice(0, 3).map((alert, index) => (
              <Alert 
                key={index} 
                severity={alert.type} 
                variant="outlined"
                sx={{ fontSize: '0.875rem', mb: 1 }}
              >
                <strong>{alert.title}:</strong> {alert.message}
              </Alert>
            ))}
          </Box>
        )}

        {/* Productos críticos */}
        <Typography variant="h6" gutterBottom>
          Productos Prioritarios
        </Typography>
        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
          {processedProducts.slice(0, 5).map((product, index) => (
            <React.Fragment key={product.id || index}>
              <ProductStockItem 
                product={product} 
                onAction={onProductAction}
              />
              {index < processedProducts.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 2 }} />
          
          {/* Todos los productos */}
          <Typography variant="h6" gutterBottom>
            Inventario Completo
          </Typography>
          <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
            {processedProducts.map((product, index) => (
              <React.Fragment key={product.id || index}>
                <ProductStockItem 
                  product={product} 
                  onAction={onProductAction}
                />
                {index < processedProducts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          {/* Recomendaciones */}
          {allRecommendations.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Recomendaciones
              </Typography>
              {allRecommendations.map((rec, index) => (
                <Alert 
                  key={index} 
                  severity="info" 
                  variant="outlined"
                  sx={{ fontSize: '0.875rem', mb: 1 }}
                >
                  <strong>{rec.title}:</strong> {rec.description}
                </Alert>
              ))}
            </>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

InventoryInsightsCard.propTypes = {
  data: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      stock: PropTypes.number,
      minStock: PropTypes.number,
      maxStock: PropTypes.number,
      turnoverRate: PropTypes.number,
      daysUntilStockout: PropTypes.number
    })),
    metrics: PropTypes.shape({
      totalProducts: PropTypes.number,
      lowStockProducts: PropTypes.number,
      outOfStockProducts: PropTypes.number,
      overStockProducts: PropTypes.number,
      averageTurnover: PropTypes.number,
      inventoryValue: PropTypes.number
    }),
    alerts: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.oneOf(['error', 'warning', 'info', 'success']),
      title: PropTypes.string,
      message: PropTypes.string
    })),
    recommendations: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string
    }))
  }),
  onProductAction: PropTypes.func
};

export default InventoryInsightsCard;
